import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { App, Button, Checkbox, Flex, Skeleton } from "antd"
import { useState } from "react"
import { useTheme } from "styled-components"
import { ShowMessagePage } from "../config"
import { getDropboxes, updateDropbox } from "../endpoints"
import { DropboxInterface } from "../types"

import { EditOutlined } from "@ant-design/icons"
import Table, { ColumnsType } from "antd/es/table"
import { ContainerFull, LabeledBox, Padded, RowInput } from "../components/GeneralComponents"

export const Dropboxes = (): JSX.Element => {
    const { modal } = App.useApp()
    const queryClient = useQueryClient()

    const theme = useTheme()
    const [selectedRow, setSelectedRow] = useState<number>(-1)

    const [editorMode, setEditorMode] = useState<string>("none")

    const [dropboxId, setDropboxId] = useState<number | null>(null)
    const [label, setLabel] = useState<string | null>()
    const [addressid, setAddressid] = useState<number | null>()
    const [active, setActive] = useState<boolean | null>(null)
    const [notes, setNotes] = useState<string | null>(null)
    const [ts, setTs] = useState<string | null>(null)
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getdropboxes"],
        queryFn: getDropboxes,
    })

    function setDropboxForEdit(i: number) {
        const currentDropbox = data?.dropboxes[i]

        if (currentDropbox) {
            // console.log(currentDropbox)
            setDropboxId(currentDropbox.id ?? null)
            setLabel(currentDropbox.label)
            setAddressid(currentDropbox.addressid)
            setNotes(currentDropbox.notes)
            setTs(currentDropbox.ts ?? "")
            setActive(currentDropbox.active ?? null)
            setEditorMode("edit")
        }
    }
    const updateMutation = useMutation({
        mutationFn: updateDropbox,
        onSuccess: (data) => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ["getdropboxes"] })
            // console.log(data)
            if (data.code !== 1000)
                modal.warning({
                    content: "Failed to create or update dropbox. " + data.message,
                })
            else setEditorMode("none")
        },
        onError: (error) => {
            modal.error({ content: error.message })
        },
    })

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "unknown error")
    }
    if (data?.code !== 1000) {
        return <div>error in getting dropbox data</div>
    }

    // consolelog('data',data)
    const columns: ColumnsType<DropboxInterface> = [
        {
            title: "Edit",
            dataIndex: "id",
            render: (value, record, index) => {
                return (
                    <EditOutlined
                        style={{ color: "blue" }}
                        onClick={async () => {
                            // consolelog("index clicked:", index)
                            setDropboxForEdit(index)
                        }}
                    />
                )
            },
        },
        {
            title: "ID",
            dataIndex: "id",
            render: (text, record, index) => {
                return <span>{record.id}</span>
            },
        },
        {
            title: "Label",
            dataIndex: "label",
            render: (text, record, index) => {
                return <span>{record.label}</span>
            },
        },
        {
            title: "Active",
            dataIndex: "active",
            render: (text, record, index) => {
                return <span>{record.active ? "Y" : "N"}</span>
            },
        },

        {
            title: "Notes",
            dataIndex: "notes",
            render: (text, record, index) => {
                return <span>{record.notes}</span>
            },
        },

        {
            title: "ts",
            dataIndex: "ts",
            render: (text, record, index) => {
                return <span>{record.ts ? new Date(record.ts).toLocaleString() : ""}</span>
            },
        },
        {
            title: "Address",
            dataIndex: "address",
            render: (text, record, index) => {
                return (
                    <span>
                        {record.address?.line1 +
                            " " +
                            record.address?.line2 +
                            " " +
                            record.address?.city +
                            " " +
                            record.address?.state +
                            " " +
                            record.address?.country +
                            " " +
                            record.address?.zip}
                    </span>
                )
            },
        },
    ]
    // consolelog('promotionsData',promotionsData)

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <h2>Dropboxes</h2>
            <Table
                dataSource={data.dropboxes?.map((row) => {
                    return { ...row, key: row.id }
                })}
                rowClassName={(record, index) =>
                    index === selectedRow ? "highlighted-table-row" : "regular-table-row"
                }
                columns={columns}
                pagination={{ position: ["bottomLeft"], pageSize: 20 }}
                size="middle"
                onRow={(record, index) => {
                    return {
                        onClick: () => {
                            if (index === selectedRow) {
                                setSelectedRow(-1)
                            } else {
                                setSelectedRow(index ?? -1)
                            }
                        },
                    }
                }}
            />
            <Button
                size="large"
                type="primary"
                style={{
                    fontSize: "1rem",
                    // fontWeight: "bold",
                    width: "16em",
                    color: theme.primary,
                    backgroundColor: theme.white,
                    boxShadow: "1px 1px 2px 2px rgba(0, 0, 0, 0.5)",
                }}
                icon={
                    <span
                        className="material-symbols-outlined"
                        style={{
                            verticalAlign: "middle",
                            fontSize: "1rem",
                            color: theme.secondary,
                        }}
                    >
                        add
                    </span>
                }
                onClick={() => {
                    setDropboxId(null)
                    setLabel("")
                    setAddressid(null)
                    setNotes("")

                    setEditorMode("new")
                }}
            >
                Add a Dropbox
            </Button>

            {editorMode !== "none" && (
                <LabeledBox title="Enter Dropbox Information" border>
                    <Flex vertical gap={20}>
                        <Padded style={{ marginBottom: "-1em" }}>Dropbox ID: {dropboxId}</Padded>
                        <RowInput
                            style={{ color: "black", maxWidth: "30em" }}
                            addonBefore="Dropbox Label:"
                            id="label"
                            value={label ?? ""}
                            onChange={(e) => {
                                console.log(e.target.value)
                                setLabel(e.target.value)
                            }}
                            size="middle"
                            name="code"
                        />
                        <RowInput
                            style={{ color: "black", maxWidth: "30em" }}
                            addonBefore="Address ID:"
                            id="addressid"
                            value={addressid ?? ""}
                            onChange={(e) => {
                                console.log(e.target.value)
                                setAddressid(Number(e.target.value))
                            }}
                            size="middle"
                            name="code"
                        />
                        <Checkbox
                            checked={active ?? undefined}
                            onChange={(e) => {
                                setActive(e.target.checked)
                            }}
                        >
                            Active
                        </Checkbox>
                        <RowInput
                            style={{ color: "black" }}
                            addonBefore="Notes:"
                            id="notes"
                            value={notes ?? ""}
                            onChange={(e) => {
                                console.log(e.target.value)
                                setNotes(e.target.value)
                            }}
                            size="middle"
                            name="code"
                        />
                        {dropboxId && <Padded style={{ marginBottom: "-1em" }}>Creation timestamp: {ts}</Padded>}
                        <Flex justify="center" gap="large">
                            <Button
                                size="large"
                                style={{
                                    fontSize: "1.618rem",
                                    // fontWeight: "bold",

                                    boxShadow: "1px 1px 2px 2px rgba(0, 0, 0, 0.5)",
                                }}
                                onClick={async () => {
                                    setEditorMode("none")
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="large"
                                type="primary"
                                style={{
                                    fontSize: "1rem",
                                    // fontWeight: "bold",
                                    backgroundColor: theme.primary,
                                    boxShadow: "1px 1px 2px 2px rgba(0, 0, 0, 0.5)",
                                }}
                                icon={
                                    <span
                                        className="material-symbols-outlined"
                                        style={{
                                            verticalAlign: "middle",
                                            fontSize: "1rem",
                                            color: "white",
                                        }}
                                    >
                                        file_save
                                    </span>
                                }
                                onClick={async () => {
                                    const dropbox: DropboxInterface = {
                                        id: dropboxId ?? undefined,
                                        label: label ?? "",
                                        addressid: addressid ?? -1,
                                        active: active ?? undefined,
                                        notes: notes ?? "",
                                    }
                                    // consolelog('dropbox value',dropbox)
                                    if (!dropbox.label) {
                                        modal.warning({ content: "label cannot be empty" })
                                    } else if (!dropbox.addressid) {
                                        modal.warning({ content: "addressid cannot be empty" })
                                    } else {
                                        updateMutation.mutate({
                                            dropbox,
                                        })
                                    }
                                }}
                            >
                                Save
                            </Button>
                        </Flex>
                    </Flex>
                </LabeledBox>
            )}
        </ContainerFull>
    )
}
